/* istanbul ignore file */
import { useCallback, useEffect, useRef, useState } from 'react';

import { useDeviceSize } from '@amzn/sss-website-theme';

const FADE_OUT_DURATION_MS = 600;

interface UseScrollPauseFadeOutOptions {
  /** Duration in ms to lock scroll after the element reaches the top. Default: 600 */
  lockDuration?: number;
}

/**
 * Locks body scroll briefly when the observed element's top edge reaches the viewport top,
 * triggers a fade-out state, then unlocks scroll so the user can continue.
 *
 * Uses a scroll event listener to minimise momentum overshoot, plus a snap correction at
 * trigger time for pixel-perfect positioning regardless of scroll velocity.
 *
 * The lock uses the position-preserving "fixed body" technique (not body height clamping)
 * so scroll position is preserved exactly across any viewport size. This avoids layout
 * shifts that would otherwise retrigger the animation in a loop on shorter viewports.
 *
 * Returns:
 * - ref: attach to the element you want to observe
 * - isFadingOut: boolean to drive CSS opacity transitions
 * - hasFadedOut: boolean that stays true after the animation completes
 */
export const useScrollPauseFadeOut = ({ lockDuration = FADE_OUT_DURATION_MS }: UseScrollPauseFadeOutOptions = {}) => {
  const ref = useRef<HTMLDivElement>(null);
  const hasTriggered = useRef(false);
  const lockedScrollY = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [hasFadedOut, setHasFadedOut] = useState(false);
  const { isLargeDevice } = useDeviceSize();

  const lockScroll = useCallback(() => {
    lockedScrollY.current = window.scrollY;
    const { body } = document;
    const html = document.documentElement;
    body.style.position = 'fixed';
    body.style.top = `-${lockedScrollY.current}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    // Preserve the scrollbar while body is fixed. Without this, <html> has no content
    // to scroll and the browser removes the scrollbar track, causing a layout shift.
    // A few pixels of extra height is enough to keep the scrollbar visible.
    html.style.minHeight = 'calc(100vh + 2px)';
  }, []);

  const unlockScroll = useCallback(() => {
    const { body } = document;
    const html = document.documentElement;
    body.style.removeProperty('position');
    body.style.removeProperty('top');
    body.style.removeProperty('left');
    body.style.removeProperty('right');
    body.style.removeProperty('width');
    html.style.removeProperty('min-height');
    // Restore the exact pre-lock scroll position (fixed positioning resets it to 0).
    // Temporarily disable CSS smooth scrolling so this snap is instant — otherwise
    // the app's global `html { scroll-behavior: smooth }` would animate the restore.
    const prev = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo(0, lockedScrollY.current);
    html.style.scrollBehavior = prev;
  }, []);

  const trigger = useCallback(
    (rectTop: number) => {
      hasTriggered.current = true;

      // Only lock scroll on large devices (desktop); on smaller viewports the
      // fixed-body technique causes jarring layout shifts.
      if (!isLargeDevice) {
        setIsFadingOut(true);
        setHasFadedOut(true);
        return;
      }

      const navbar = document.querySelector('#sc-navbar-container') as HTMLElement | undefined;
      if (navbar) {
        navbar.style.transform = 'translateY(-100%)';
      }

      // Snap to exact target position before locking. With a scroll listener the overshoot
      // is typically a few pixels (1 frame of momentum), so this correction is imperceptible.
      // Temporarily disable CSS smooth scrolling so the snap is instant.
      if (rectTop < 0) {
        const html = document.documentElement;
        const prev = html.style.scrollBehavior;
        html.style.scrollBehavior = 'auto';
        window.scrollTo(0, window.scrollY + rectTop);
        html.style.scrollBehavior = prev;
      }

      setIsFadingOut(true);
      lockScroll();

      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        unlockScroll();
        window.scrollTo(0, window.scrollY);
        setHasFadedOut(true);
        navbar?.style.removeProperty('transform');
      }, lockDuration);
    },
    [lockScroll, unlockScroll, lockDuration, isLargeDevice]
  );

  const reset = useCallback(() => {
    hasTriggered.current = false;
    setIsFadingOut(false);
    setHasFadedOut(false);
  }, []);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const handleScroll = () => {
      const rect = element.getBoundingClientRect();

      if (!hasTriggered.current) {
        // Trigger the moment the element's top edge reaches (or crosses) the viewport top.
        if (rect.top <= 0) {
          trigger(rect.top);
        }
      } else if (rect.top > 1) {
        // User scrolled back up and the element's top is visible again — reset so the
        // animation can play again on the next downward pass.
        reset();
      }
    };

    // Passive listener for performance; browsers coalesce scroll events to ~1 per frame.
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timerRef.current != null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        unlockScroll();
        const navbar = document.querySelector('#sc-navbar-container') as HTMLElement | null;
        navbar?.style.removeProperty('transform');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, reset]);

  return { ref, isFadingOut, hasFadedOut };
};
